import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Scale, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TYPES = [
  { v: "asset", l: "أصول" },
  { v: "liability", l: "خصوم" },
  { v: "equity", l: "حقوق ملكية" },
  { v: "revenue", l: "إيرادات" },
  { v: "expense", l: "مصروفات" },
];

export default function Accounting() {
  const { currentCompany } = useAuth();
  const cur = currentCompany?.base_currency || "SYP";
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [tb, setTb] = useState(null);
  const [cashBoxes, setCashBoxes] = useState([]);
  const [openAcc, setOpenAcc] = useState(false);
  const [accForm, setAccForm] = useState({ code: "", name: "", type: "asset", parent_code: "" });
  const [openEntry, setOpenEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ date: new Date().toISOString().slice(0,10), description: "", reference: "", lines: [{account_code:"", account_name:"", debit:0, credit:0, description:"", cash_box_id:""}, {account_code:"", account_name:"", debit:0, credit:0, description:"", cash_box_id:""}] });
  const [ledgerAcc, setLedgerAcc] = useState("");
  const [ledger, setLedger] = useState(null);
  const [lFrom, setLFrom] = useState("");
  const [lTo, setLTo] = useState("");
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [journalFailures, setJournalFailures] = useState([]);

  const loadLedger = async (code) => {
    const acc = code || ledgerAcc;
    if (!acc) return;
    setLedgerLoading(true);
    try {
      const { data } = await api.get("/journal/ledger", { params: { account_code: acc, from_date: lFrom || undefined, to_date: lTo || undefined } });
      setLedger(data);
    } catch(e) { toast.error(formatErr(e)); } finally { setLedgerLoading(false); }
  };

  const load = async () => {
    try {
      const [a, e, t, cb, failures] = await Promise.all([
        api.get("/journal/accounts"),
        api.get("/journal/entries"),
        api.get("/journal/trial-balance"),
        api.get("/cashboxes").catch(()=>({data:[]})),
        api.get("/journal/failures").catch(()=>({data:[]})),
      ]);
      setAccounts(a.data); setEntries(e.data); setTb(t.data); setCashBoxes(cb.data || []); setJournalFailures(failures.data || []);
    } catch(err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); }, [currentCompany?.id]); // eslint-disable-line

  const saveAccount = async () => {
    try {
      await api.post("/journal/accounts", { ...accForm, parent_code: accForm.parent_code || null });
      toast.success("تم إنشاء الحساب");
      setOpenAcc(false); setAccForm({ code:"", name:"", type:"asset", parent_code:"" }); load();
    } catch(e) { toast.error(formatErr(e)); }
  };

  const delAccount = async (id) => {
    if (!confirm("حذف الحساب؟")) return;
    try { await api.delete(`/journal/accounts/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const updateLine = (i, patch) => {
    setEntryForm(f => ({...f, lines: f.lines.map((l,x)=> {
      if (x !== i) return l;
      const merged = {...l, ...patch};
      if (patch.account_code) {
        const acc = accounts.find(a => a.code === patch.account_code);
        if (acc) merged.account_name = acc.name;
      }
      return merged;
    })}));
  };

  const addLine = () => setEntryForm(f => ({...f, lines: [...f.lines, {account_code:"", account_name:"", debit:0, credit:0, description:"", cash_box_id:""}]}));
  const rmLine = (i) => setEntryForm(f => ({...f, lines: f.lines.filter((_,x)=>x!==i)}));

  // Is this account code a Cash-family account that needs cashbox selection?
  const isCashCode = (code) => code === "1100";
  const cashLinesMissingBox = entryForm.lines.some(l => isCashCode(l.account_code) && ((+l.debit||0) + (+l.credit||0)) > 0 && !l.cash_box_id);

  const debitTotal = entryForm.lines.reduce((s,l)=>s+(+l.debit||0), 0);
  const creditTotal = entryForm.lines.reduce((s,l)=>s+(+l.credit||0), 0);
  const balanced = Math.abs(debitTotal - creditTotal) < 0.01;
  const canSave = balanced && !cashLinesMissingBox;

  const saveEntry = async () => {
    if (!balanced) return toast.error("القيد غير متوازن");
    try {
      const body = {...entryForm, lines: entryForm.lines.map(l => ({...l, debit:+l.debit||0, credit:+l.credit||0}))};
      await api.post("/journal/entries", body);
      toast.success("تم حفظ القيد");
      setOpenEntry(false);
      setEntryForm({ date: new Date().toISOString().slice(0,10), description: "", reference: "", lines: [{account_code:"", account_name:"", debit:0, credit:0, description:"", cash_box_id:""}, {account_code:"", account_name:"", debit:0, credit:0, description:"", cash_box_id:""}]});
      load();
    } catch(e) { toast.error(formatErr(e)); }
  };

  const delEntry = async (id) => {
    if (!confirm("حذف القيد؟")) return;
    try { await api.delete(`/journal/entries/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">القيود المحاسبية</h1>
        <p className="text-sm text-muted-foreground mt-1">دليل الحسابات · القيود اليومية · ميزان المراجعة</p>
      </div>
      {journalFailures.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900" data-testid="journal-failures-alert">
          <div className="flex items-center gap-2 font-bold text-sm"><AlertTriangle className="w-4 h-4" /> توجد {journalFailures.length} معاملات لم يُرحّل قيدها التلقائي</div>
          <div className="mt-2 space-y-1 text-xs">
            {journalFailures.slice(0, 5).map(f => <div key={f.id} className="flex justify-between gap-3"><span>{f.description || f.source_type}</span><span className="text-amber-700">{f.reason}</span></div>)}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="accounts" data-testid="tab-accounts"><BookOpen className="w-4 h-4 me-1" /> دليل الحسابات</TabsTrigger>
          <TabsTrigger value="entries" data-testid="tab-entries">القيود</TabsTrigger>
          <TabsTrigger value="trial" data-testid="tab-trial"><Scale className="w-4 h-4 me-1" /> ميزان المراجعة</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><BookOpen className="w-4 h-4 me-1" /> دفتر الأستاذ</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <div className="flex justify-end mb-2">
            <Dialog open={openAcc} onOpenChange={setOpenAcc}>
              <DialogTrigger asChild><Button data-testid="add-account-button"><Plus className="w-4 h-4 me-1" /> حساب جديد</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>حساب جديد</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>الرمز</Label><Input value={accForm.code} onChange={(e)=>setAccForm({...accForm, code:e.target.value})} data-testid="account-code-input" /></div>
                  <div><Label>النوع</Label>
                    <Select value={accForm.type} onValueChange={(v)=>setAccForm({...accForm, type:v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>الاسم</Label><Input value={accForm.name} onChange={(e)=>setAccForm({...accForm, name:e.target.value})} data-testid="account-name-input" /></div>
                  <div className="col-span-2"><Label>الحساب الأب (اختياري)</Label><Input value={accForm.parent_code} onChange={(e)=>setAccForm({...accForm, parent_code:e.target.value})} placeholder="مثال: 1000" /></div>
                </div>
                <DialogFooter><Button onClick={saveAccount} data-testid="save-account-button">حفظ</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="border border-border rounded-md bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>الرمز</TableHead><TableHead>الاسم</TableHead><TableHead>النوع</TableHead><TableHead>الأب</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {accounts.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono font-bold">{a.code}</TableCell>
                    <TableCell className="font-semibold">{a.name}</TableCell>
                    <TableCell><span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">{TYPES.find(t=>t.v===a.type)?.l}</span></TableCell>
                    <TableCell className="text-xs">{a.parent_code||"—"}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={()=>delAccount(a.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="entries">
          <div className="flex justify-end mb-2">
            <Dialog open={openEntry} onOpenChange={setOpenEntry}>
              <DialogTrigger asChild><Button data-testid="add-entry-button"><Plus className="w-4 h-4 me-1" /> قيد جديد</Button></DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>قيد يومية جديد</DialogTitle></DialogHeader>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div><Label>التاريخ</Label><Input type="date" value={entryForm.date} onChange={(e)=>setEntryForm({...entryForm, date:e.target.value})} /></div>
                  <div className="col-span-2"><Label>البيان</Label><Input value={entryForm.description} onChange={(e)=>setEntryForm({...entryForm, description:e.target.value})} data-testid="entry-description-input" /></div>
                </div>
                <div className="border border-border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>الحساب</TableHead><TableHead className="w-32">مدين</TableHead><TableHead className="w-32">دائن</TableHead><TableHead>الصندوق</TableHead><TableHead>البيان</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {entryForm.lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Select value={l.account_code} onValueChange={(v)=>updateLine(i, {account_code:v, cash_box_id: isCashCode(v) ? l.cash_box_id : ""})}>
                              <SelectTrigger className="w-full"><SelectValue placeholder="اختر حساباً" /></SelectTrigger>
                              <SelectContent>
                                {accounts.map(a => <SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell><Input type="number" step="0.01" value={l.debit} onChange={(e)=>updateLine(i, {debit:+e.target.value||0, credit:0})} className="tabular-nums" /></TableCell>
                          <TableCell><Input type="number" step="0.01" value={l.credit} onChange={(e)=>updateLine(i, {credit:+e.target.value||0, debit:0})} className="tabular-nums" /></TableCell>
                          <TableCell>
                            {isCashCode(l.account_code) ? (
                              <Select value={l.cash_box_id || ""} onValueChange={(v)=>updateLine(i, {cash_box_id: v})}>
                                <SelectTrigger className={`w-full ${!l.cash_box_id ? "border-destructive" : ""}`} data-testid={`entry-cashbox-${i}`}>
                                  <SelectValue placeholder="حدد الصندوق *" />
                                </SelectTrigger>
                                <SelectContent>
                                  {cashBoxes.length === 0 && <div className="p-2 text-xs text-muted-foreground">لا توجد صناديق نشطة</div>}
                                  {cashBoxes.map(b => (
                                    <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency}) · رصيد: {b.balance}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell><Input value={l.description||""} onChange={(e)=>updateLine(i, {description:e.target.value})} /></TableCell>
                          <TableCell><Button size="icon" variant="ghost" onClick={()=>rmLine(i)}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {cashLinesMissingBox && (
                  <div className="mt-2 p-2 bg-destructive/10 border border-destructive/30 rounded text-xs text-destructive font-semibold" data-testid="cashbox-warning">
                    ⚠️ يجب تحديد الصندوق النقدي لكل سطر يمس حساب الصندوق (كود 1100)
                  </div>
                )}
                <div className="flex justify-between items-center mt-3">
                  <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-4 h-4 me-1" /> سطر</Button>
                  <div className="flex gap-4 text-sm">
                    <span>مدين: <span className="tabular-nums font-bold">{debitTotal.toFixed(2)}</span></span>
                    <span>دائن: <span className="tabular-nums font-bold">{creditTotal.toFixed(2)}</span></span>
                    <span className={`font-bold ${balanced ? "text-emerald-600" : "text-destructive"}`}>{balanced ? "✓ متوازن" : "✗ غير متوازن"}</span>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveEntry} disabled={!canSave} data-testid="save-entry-button">حفظ القيد</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="border border-border rounded-md bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>رقم</TableHead><TableHead>التاريخ</TableHead><TableHead>البيان</TableHead><TableHead>المدين</TableHead><TableHead>الدائن</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد قيود</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono">#{e.number}</TableCell>
                    <TableCell className="text-xs">{e.date}</TableCell>
                    <TableCell className="font-semibold">{e.description}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(e.debit_total, cur)}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(e.credit_total, cur)}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={()=>delEntry(e.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="trial">
          <div className="border border-border rounded-md bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>الرمز</TableHead><TableHead>الحساب</TableHead><TableHead>النوع</TableHead><TableHead>المدين</TableHead><TableHead>الدائن</TableHead><TableHead>الرصيد</TableHead></TableRow></TableHeader>
              <TableBody>
                {tb?.rows?.filter(r => r.debit || r.credit).map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono">{r.code}</TableCell>
                    <TableCell className="font-semibold">{r.name}</TableCell>
                    <TableCell className="text-xs">{TYPES.find(t=>t.v===r.type)?.l}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(r.debit, cur)}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(r.credit, cur)}</TableCell>
                    <TableCell className={`tabular-nums font-bold ${r.balance>=0?"text-emerald-700":"text-destructive"}`}>{fmtMoney(r.balance, cur)}</TableCell>
                  </TableRow>
                ))}
                {(!tb?.rows?.some(r => r.debit || r.credit)) && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد قيود بعد</TableCell></TableRow>}
              </TableBody>
              {tb?.rows?.some(r => r.debit || r.credit) && (
                <TableBody>
                  <TableRow className="font-bold bg-muted">
                    <TableCell colSpan={3} className="text-end">الإجمالي</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(tb?.debit_total, cur)}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(tb?.credit_total, cur)}</TableCell>
                    <TableCell className={`tabular-nums ${Math.abs((tb?.debit_total||0)-(tb?.credit_total||0)) < 0.01 ? "text-emerald-600" : "text-destructive"}`}>{Math.abs((tb?.debit_total||0)-(tb?.credit_total||0)) < 0.01 ? "متوازن" : "غير متوازن"}</TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="ledger">
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="min-w-[240px]">
              <Label>الحساب</Label>
              <Select value={ledgerAcc} onValueChange={(v)=>{ setLedgerAcc(v); loadLedger(v); }}>
                <SelectTrigger data-testid="ledger-account-select"><SelectValue placeholder="اختر حساباً لعرض حركته" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => <SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>من</Label><Input type="date" value={lFrom} onChange={(e)=>setLFrom(e.target.value)} data-testid="ledger-from" /></div>
            <div><Label>إلى</Label><Input type="date" value={lTo} onChange={(e)=>setLTo(e.target.value)} data-testid="ledger-to" /></div>
            <Button variant="outline" onClick={()=>loadLedger()} disabled={!ledgerAcc || ledgerLoading} data-testid="ledger-refresh">عرض</Button>
          </div>

          {!ledger && <div className="text-center py-10 text-muted-foreground">اختر حساباً لعرض دفتر الأستاذ الخاص به.</div>}
          {ledger && (
            <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="ledger-table">
              <div className="p-3 bg-muted/50 flex flex-wrap justify-between items-center gap-2">
                <div className="font-heading font-bold">{ledger.account.code} — {ledger.account.name}</div>
                <div className="text-sm">الرصيد الافتتاحي: <span className="font-mono font-bold">{fmtMoney(ledger.opening_balance, cur)}</span></div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>التاريخ</TableHead><TableHead>القيد</TableHead><TableHead>البيان</TableHead><TableHead>مدين</TableHead><TableHead>دائن</TableHead><TableHead>الرصيد</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={5} className="text-xs font-semibold text-muted-foreground">رصيد ما قبل الفترة</TableCell>
                    <TableCell className="tabular-nums font-bold">{fmtMoney(ledger.opening_balance, cur)}</TableCell>
                  </TableRow>
                  {ledger.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد حركة في هذه الفترة</TableCell></TableRow>}
                  {ledger.rows.map((r, i) => (
                    <TableRow key={i} data-testid={`ledger-row-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                      <TableCell className="font-mono text-xs">#{r.number}</TableCell>
                      <TableCell className="font-semibold">{r.description}{r.reference ? <span className="text-xs text-muted-foreground"> · {r.reference}</span> : null}</TableCell>
                      <TableCell className="tabular-nums">{r.debit ? fmtMoney(r.debit, cur) : "—"}</TableCell>
                      <TableCell className="tabular-nums">{r.credit ? fmtMoney(r.credit, cur) : "—"}</TableCell>
                      <TableCell className={`tabular-nums font-bold ${r.balance>=0?"text-emerald-700":"text-destructive"}`}>{fmtMoney(r.balance, cur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableBody>
                  <TableRow className="font-bold bg-muted">
                    <TableCell colSpan={3} className="text-end">إجمالي الحركة / الرصيد الختامي</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(ledger.debit_total, cur)}</TableCell>
                    <TableCell className="tabular-nums">{fmtMoney(ledger.credit_total, cur)}</TableCell>
                    <TableCell className={`tabular-nums ${ledger.closing_balance>=0?"text-emerald-700":"text-destructive"}`}>{fmtMoney(ledger.closing_balance, cur)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
